import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * TokenLogo component for displaying token logos consistently throughout the application
 * 
 * @param {Object} props - Component props
 * @param {Object} props.token - Token object with address, symbol, and name properties
 * @param {string} props.size - Size of the logo (sm, md, lg)
 * @param {boolean} props.showSymbol - Whether to show the symbol next to the logo
 * @param {boolean} props.clickable - Whether the logo is clickable and navigates to the token page
 * @param {string} props.className - Additional CSS classes
 */
const TokenLogo = ({ 
  token, 
  size = 'md', 
  showSymbol = false, 
  clickable = false,
  className = '' 
}) => {
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();
  
  // Size classes
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-lg'
  };
  
  // Get token symbol (first 2 characters or fallback)
  const getSymbolDisplay = () => {
    if (!token) return 'T';
    if (token.symbol) return token.symbol.substring(0, 2).toUpperCase();
    if (token.name) return token.name.substring(0, 2).toUpperCase();
    return 'T';
  };
  
  // Try to load the token logo from the public directory
  useEffect(() => {
    if (!token || !token.address) {
      console.warn('TokenLogo: No token or token address provided', token);
      return;
    }
    
    // Reset state when token changes
    setLogoError(false);
    setLogoUrl(null);
    
    // Try to load the logo from the public directory
    const tokenAddress = token.address.toLowerCase();
    
    console.log(`TokenLogo: Attempting to load token logo for ${token.symbol || 'unknown'} (${tokenAddress})`);
    
    // Special cases for common tokens to prevent flickering
    if (token.symbol === '3DC' || tokenAddress === '0xb7cf94ee2b010ff69ea4f67a7d951c0cd8936170') {
      console.log('TokenLogo: Using 3DC logo from /3dc-logo.png');
      setLogoUrl('/3dc-logo.png');
      return;
    }
    
    // Special case for WSTO (Wrapped Studio)
    if (token.symbol === 'WSTO' || token.name === 'Wrapped Studio' || tokenAddress === '0x18ba8b66cf799c23ae5c3728ad3d4d9017213149') {
      console.log('TokenLogo: Using WSTO logo from /studio-logo.png');
      setLogoUrl('/studio-logo.png');
      return;
    }
    
    // Special case for USDT
    if (token.symbol === 'USDT' || tokenAddress === '0x416a4b8365DEAF14452cf40c73065A4BC1EbF066') {
      console.log('TokenLogo: Using USDT logo from /tether-usdt-logo.png');
      setLogoUrl('/tether-usdt-logo.png');
      return;
    }
    
    // Special case for USDC
    if (token.symbol === 'USDC' || tokenAddress === '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48') {
      console.log('TokenLogo: Using USDC logo from /usdc-logo.png');
      setLogoUrl('/usdc-logo.png');
      return;
    }
    
    // Special case for ADA
    if (token.symbol === 'ADA' || tokenAddress === '0x670e09c73f34ba775a0e5422df6cf09c9dd5cabc') {
      console.log('TokenLogo: Using ADA logo from /ada.png');
      setLogoUrl('/ada.png');
      return;
    }
    
    // Special case for TRX
    if (token.symbol === 'TRX' || tokenAddress === '0x7086c7c9108e0ac7736fcbf19bf0937ef019b8f3') {
      console.log('TokenLogo: Using TRX logo from /TRX.png');
      setLogoUrl('/TRX.png');
      return;
    }
    
    // Special case for BTC
    if (token.symbol === 'BTC' || tokenAddress === '0x76eaa2c476db78ad503ce24c4cf3c595651abb47') {
      console.log('TokenLogo: Using BTC logo from /btc.png');
      setLogoUrl('/btc.png');
      return;
    }
    
    // Special case for LINK
    if (token.symbol === 'LINK' || tokenAddress === '0xd9c4be029295e717b23d6911444b0e5e830cd959') {
      console.log('TokenLogo: Using LINK logo from /LINK.png');
      setLogoUrl('/LINK.png');
      return;
    }
    
    // Special case for AVAX
    if (token.symbol === 'AVAX' || tokenAddress === '0x0fff976c1a92413c89fd0a5da55a46091a19db14') {
      console.log('TokenLogo: Using AVAX logo from /AVAX.png');
      setLogoUrl('/AVAX.png');
      return;
    }
    
    // Special case for XLM
    if (token.symbol === 'XLM' || tokenAddress === '0xb267b51a12e54e4f4ec29dc142ebc4121f78b58e') {
      console.log('TokenLogo: Using XLM logo from /XLM.png');
      setLogoUrl('/XLM.png');
      return;
    }
    
    // Special case for TON
    if (token.symbol === 'TON' || tokenAddress === '0x5b1e2036701bbb5bd26b073a2d4c23d42629efc0') {
      console.log('TokenLogo: Using TON logo from /toncoin.png');
      setLogoUrl('/toncoin.png');
      return;
    }
    
    // Special case for DOT
    if (token.symbol === 'DOT' || tokenAddress === '0x5e34d88740791f5d4539f2ee1f21d745fd105c7a') {
      console.log('TokenLogo: Using DOT logo from /DOT.png');
      setLogoUrl('/DOT.png');
      return;
    }
    
    // Special case for LTC
    if (token.symbol === 'LTC' || tokenAddress === '0x2575054bde53c8e6b1ef4c065fcd9eafac686fc9') {
      console.log('TokenLogo: Using LTC logo from /LTC.png');
      setLogoUrl('/LTC.png');
      return;
    }
    
    // Special case for PEPE
    if (token.symbol === 'PEPE' || tokenAddress === '0x6a5bd07bae7f3a118625f53f137578f2186ab10f') {
      console.log('TokenLogo: Using PEPE logo from /PEPE.png');
      setLogoUrl('/PEPE.png');
      return;
    }
    
    // Special case for DAI
    if (token.symbol === 'DAI' || tokenAddress === '0x502e28a890379e14c1ac48c8211a17de90ab4908') {
      console.log('TokenLogo: Using DAI logo from /DAI.png');
      setLogoUrl('/DAI.png');
      return;
    }
    
    // Special case for AAVE
    if (token.symbol === 'AAVE' || tokenAddress === '0xf02525bff49f68ba5b212101b6e42fabeaf51929') {
      console.log('TokenLogo: Using AAVE logo from /AAVE.png');
      setLogoUrl('/AAVE.png');
      return;
    }
    
    // Special case for NEAR
    if (token.symbol === 'NEAR' || tokenAddress === '0x17774452905c45c21a4dfc5607040093e9cc0b07') {
      console.log('TokenLogo: Using NEAR logo from /NEAR.png');
      setLogoUrl('/NEAR.png');
      return;
    }
    
    // Special case for FET
    if (token.symbol === 'FET' || tokenAddress === '0xaf9fc8a635dca9f0969071bb29d6c7911cbd2b25') {
      console.log('TokenLogo: Using FET logo from /FET.png');
      setLogoUrl('/FET.png');
      return;
    }
    
    // Special case for VET
    if (token.symbol === 'VET' || tokenAddress === '0x40fa86b63e0a4f6d6480d2a615cbe8aec9593b2d') {
      console.log('TokenLogo: Using VET logo from /VET.png');
      setLogoUrl('/VET.png');
      return;
    }
    
    // Special case for ARB
    if (token.symbol === 'ARB' || tokenAddress === '0xcd7394d30e2cc8183aae24a3173d2a19e18c2a72') {
      console.log('TokenLogo: Using ARB logo from /ARB.png');
      setLogoUrl('/ARB.png');
      return;
    }
    
    // Special case for OP
    if (token.symbol === 'OP' || tokenAddress === '0x952d8d93dcb730450c1b56e1a6894a83bf06ffe4') {
      console.log('TokenLogo: Using OP logo from /OP.png');
      setLogoUrl('/OP.png');
      return;
    }
    
    // Special case for USD1
    if (token.symbol === 'USD1' || tokenAddress === '0xc718a5955c595d757a7f2e77dfbac6dc867029a7') {
      console.log('TokenLogo: Using USD1 logo from /USD1.png');
      setLogoUrl('/USD1.png');
      return;
    }
    
    // Special case for ETH
    if (token.symbol === 'ETH' || tokenAddress === '0x289ee4618520b2fe8f2763525f3114a71d8d4ff7') {
      console.log('TokenLogo: Using ETH logo from /ETH.png');
      setLogoUrl('/ETH.png');
      return;
    }
    
    // Special case for WBNB
    if (token.symbol === 'WBNB' || token.symbol === 'BNB' || tokenAddress === '0x6e7cce539b9b720e3d483bb3e68c0366ccde2f34') {
      console.log('TokenLogo: Using BNB logo from /bnb.png');
      setLogoUrl('/bnb.png');
      return;
    }
    
    // Special case for COOP
    if (token.symbol === 'COOP' || tokenAddress === '0x8332c045587d5d9ff3d5eec2fb21e417166b0ede') {
      console.log('TokenLogo: Using COOP logo from /coop.png');
      setLogoUrl('/coop.png');
      return;
    }
    
    // Special case for COOP-S
    if (token.symbol === 'COOP-S' || tokenAddress === '0xce6c09a662438a555af258a5fc46e6fbc1b368d3') {
      console.log('TokenLogo: Using COOP-S logo from /coop-s.png');
      setLogoUrl('/coop-s.png');
      return;
    }
    
    // Special case for GMDS
    if (token.symbol === 'GMDS' || tokenAddress === '0x648c7e61e11e2eb2750028af6b58cbc34fa35432') {
      console.log('TokenLogo: Using GMDS logo from /gmds.png');
      setLogoUrl('/gmds.png');
      return;
    }
    
    // Use a static path without timestamp to allow browser caching
    const logoPath = `/token-logos/${tokenAddress}.png`;
    
    // Check if the logo exists
    fetch(logoPath, { cache: 'force-cache' }) // Use browser cache when available
      .then(response => {
        if (response.ok) {
          console.log(`Successfully loaded token logo for ${token.symbol || 'unknown'}`);
          setLogoUrl(logoPath);
        } else {
          console.warn(`Logo not found for ${token.symbol || 'unknown'} at ${logoPath}`);
          throw new Error('Logo not found');
        }
      })
      .catch((error) => {
        console.error(`Error loading token logo for ${token.symbol || 'unknown'}:`, error);
        // If the logo doesn't exist, try to load from token.logoUrl if available
        if (token.logoUrl) {
          console.log(`Trying alternative logo URL for ${token.symbol || 'unknown'}: ${token.logoUrl}`);
          setLogoUrl(token.logoUrl);
        } else {
          console.warn(`No alternative logo URL for ${token.symbol || 'unknown'}, using fallback`);
          setLogoError(true);
        }
      });
  }, [token]);
  
  // Handle click on the logo
  const handleClick = () => {
    if (clickable && token && token.address) {
      navigate(`/tokens/${token.address}`);
    }
  };
  
  // If no token, return a placeholder
  if (!token) {
    return (
      <div className={`rounded-full bg-gray-700 flex items-center justify-center ${sizeClasses[size]} ${className}`}>
        <span className="text-gray-400">?</span>
      </div>
    );
  }
  
  // Render the component
  return (
    <div className={`flex items-center ${className}`}>
      <div 
        className={`rounded-full flex-shrink-0 flex items-center justify-center ${sizeClasses[size]} ${
          logoUrl ? '' : 'bg-primary bg-opacity-20'
        } ${clickable ? 'cursor-pointer' : ''}`}
        onClick={handleClick}
      >
        {logoUrl && !logoError ? (
          <img 
            src={logoUrl} 
            alt={token.symbol || token.name || 'Token'} 
            className="w-full h-full rounded-full object-contain"
            onError={(e) => {
              console.error(`Error loading token logo: ${logoUrl}`, e);
              // Only set error if we're not using a special case logo
              if (!logoUrl.includes('/3dc-logo.png') && 
                  !logoUrl.includes('/tether-usdt-logo.png') && 
                  !logoUrl.includes('/usdc-logo.png')) {
                setLogoError(true);
              } else {
                // For special case logos, retry once with a different approach
                const retryUrl = logoUrl.includes('?retry') ? null : `${logoUrl}?retry=true`;
                if (retryUrl) {
                  console.log(`Retrying with: ${retryUrl}`);
                  setLogoUrl(retryUrl);
                } else {
                  setLogoError(true);
                }
              }
            }}
          />
        ) : (
          <span className="font-semibold text-primary">
            {getSymbolDisplay()}
          </span>
        )}
      </div>
      
      {showSymbol && token.symbol && (
        <span className="ml-2 font-medium">
          {token.symbol}
        </span>
      )}
    </div>
  );
};

export default TokenLogo;
